// src/components/ResignedStaff.js
//
// Settings -> Resigned Staff. Everyone who has left, whatever their role was:
// a manager and someone who delivered services both appear here.
//
// They are kept, never deleted. Every service they performed and every bill
// they raised still points at them, so Staff Performance and past reports for
// earlier periods stay correct. Reinstating puts them back on the rota and
// reopens their login.

import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";

const ROLE_LABELS = { staff: "Staff", manager: "Manager", admin: "Admin" };

export default function ResignedStaff() {
  const [rows, setRows] = useState([]);
  const [canManageAdmins, setCanManageAdmins] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth("/api/manage/staff/");
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not load staff.");
        return;
      }
      setRows(js.staff || []);
      setCanManageAdmins(Boolean(js.can_manage_admins));
    } catch {
      setError("Could not reach the server.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resigned = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((r) => r.resigned_on)
      .filter((r) =>
        term
          ? (r.display_name || "").toLowerCase().includes(term) ||
            (r.username || "").toLowerCase().includes(term)
          : true
      )
      .sort((a, b) => String(b.resigned_on).localeCompare(String(a.resigned_on)));
  }, [rows, search]);

  function lockedReason(row) {
    if (!canManageAdmins && (row.role === "admin" || row.is_superuser)) {
      return "Only an admin can change an admin account.";
    }
    return "";
  }

  async function reinstate(row) {
    if (
      !window.confirm(
        `Bring ${row.display_name} back?\n\n` +
          "They go back on the rota, their login is reopened, and they appear " +
          "in staff pickers again."
      )
    ) {
      return;
    }
    setBusyId(row.id);
    setError("");
    setNotice("");
    try {
      const res = await fetchWithAuth(`/api/manage/staff/${row.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ resigned_on: null }),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not reinstate.");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === row.id ? js : r)));
      setNotice(`${js.display_name} is back on the team - see the Staff tab.`);
    } finally {
      setBusyId(null);
    }
  }

  const cell = { padding: "6px 8px", verticalAlign: "middle" };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Resigned Staff</h4>
        <input className="input" placeholder="Search by name" style={{ maxWidth: 220 }}
               value={search} onChange={(e) => setSearch(e.target.value)} />
        <button className="btn" type="button" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <span style={{ color: "#666", fontSize: 13 }}>
          {resigned.length} {resigned.length === 1 ? "person" : "people"}
        </span>
      </div>

      {error && (
        <div style={{ background: "#fdecea", border: "1px solid #f5a29b", color: "#8a1c12",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ background: "#e8f5e9", border: "1px solid #8ccf91", color: "#1b5e20",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          {notice}
        </div>
      )}

      {loading && <div>Loading...</div>}

      {!loading && resigned.length === 0 && (
        <div style={{ color: "#666" }}>
          Nobody has been marked as resigned. Use the Resign button on the Staff tab
          when someone leaves.
        </div>
      )}

      {!loading && resigned.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
                <th style={cell}>Name</th>
                <th style={cell}>Role when they left</th>
                <th style={cell}>Last working day</th>
                <th style={cell}>Login</th>
                <th style={cell}></th>
              </tr>
            </thead>
            <tbody>
              {resigned.map((row) => {
                const locked = lockedReason(row);
                return (
                  <tr key={row.id} style={{ borderBottom: "1px solid #f3f3f3" }}>
                    <td style={cell}>
                      <strong>{row.display_name}</strong>
                      <div style={{ fontSize: 11, color: "#777" }}>signed in as {row.username}</div>
                    </td>
                    <td style={cell}>{ROLE_LABELS[row.role] || row.role}</td>
                    <td style={cell}>{row.resigned_on}</td>
                    <td style={cell}>{row.can_login ? "Open" : "Closed"}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>
                      <button className="btn" type="button" onClick={() => reinstate(row)}
                              disabled={busyId === row.id || Boolean(locked)}
                              title={locked}>
                        {busyId === row.id ? "Working..." : "Reinstate"}
                      </button>
                      {locked && (
                        <div style={{ fontSize: 11, color: "#8a4b00", marginTop: 4 }}>{locked}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 12, color: "#666", marginTop: 12, lineHeight: 1.6 }}>
        Resigned staff are kept rather than deleted, so their past bills and their
        Staff Performance figures for earlier periods stay correct. They do not appear
        in staff pickers when billing or booking, and cannot be assigned to services.
      </div>
    </div>
  );
}
